import React, { useEffect, useRef, useMemo } from "react";
import styles from "./style.module.scss";
import { useLabelPropagation } from "hooks/LabelPropagation/useLabelPropagation";
import { Option } from "views/uikit/FormFields/SelectList";
import { getIsLabelPropagationEnabled } from "redux/SingleDataset/selectors";
import { useSelector } from "react-redux";
import VLButton from "views/uikit/Buttons/VLButton";
import VLColorfulLoader from "views/uikit/VLColorfulLoader";
import VLActionModal from "views/uikit/VLModal/VLActionModal";
import useAutosizerScrollStatus from "hooks/useAutosizerScrollStatus";
import { useLocation } from "react-router";
import {
  LABEL_CLASS_PARAMETER,
  REVIEW_PARAMETER,
} from "helpers/constants/miscellaneous";
import VLCount from "views/uikit/VLCount";
import classNames from "classnames";
import { Tooltip } from "antd";

interface LabelPropagationProps {
  datasetId: string;
  onDataChange: (data: {
    classesOptions: Option[];
    displayNewFloatingPanel: boolean;
  }) => void;
  assignItemsToClass?: string;
  onAssign?: (assignFunc: (className: string) => void) => void;
  initialStep?: number;
  shouldFetch?: string;
  resetShouldFetch?: () => void;
}

export default function LabelPropagation({
  datasetId,
  onDataChange,
  onAssign,
  shouldFetch,
  resetShouldFetch,
}: LabelPropagationProps) {
  const {
    loading,
    currentStep,
    step,
    selectedLabels,
    classesOptions,
    assignItemsToClassHandler,
    isLoading,
    modal,
    toggleBlinkCard,
  } = useLabelPropagation(
    datasetId,
    shouldFetch || undefined,
    resetShouldFetch,
  );
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const inReview = searchParams.has(REVIEW_PARAMETER);
  const labelId = searchParams.has(LABEL_CLASS_PARAMETER);

  const isLabelPropagationEnabled = useSelector(getIsLabelPropagationEnabled);

  // store previous values to prevent unnecessary onDataChange calls
  const prevDataRef = useRef<{ values: string[]; displayPanel: boolean }>();
  const stepContentRef = useRef<HTMLDivElement>(null);

  // memoize selected options and values to prevent new references each render
  const selectedOptions = useMemo(
    () => classesOptions.filter((opt) => selectedLabels.includes(opt.value)),
    [classesOptions, selectedLabels],
  );

  const selectedValues = useMemo(
    () => selectedOptions.map((opt) => opt.value),
    [selectedOptions],
  );

  const { isOverflowing, isAtBottom } = useAutosizerScrollStatus(
    stepContentRef,
    { current: null },
    { shouldObserveInnerRef: false },
  );

  const isContentOverflowing = isOverflowing && !isAtBottom;

  useEffect(() => {
    if (!onDataChange || !currentStep) return;

    const displayPanel = currentStep.key === 1;
    const prevData = prevDataRef.current;
    const isLabelReview = inReview && labelId;

    const hasChanged =
      !prevData ||
      prevData.displayPanel !== displayPanel ||
      isLabelReview ||
      prevData.values.length !== selectedValues.length ||
      prevData.values.some((v, i) => v !== selectedValues[i]);

    if (hasChanged) {
      prevDataRef.current = {
        values: [...selectedValues],
        displayPanel: displayPanel || isLabelReview,
      };
      onDataChange({
        classesOptions: selectedOptions,
        displayNewFloatingPanel: displayPanel || isLabelReview,
      });
    }
  }, [
    selectedValues,
    selectedOptions,
    onDataChange,
    currentStep,
    inReview,
    labelId,
  ]);
  const assignRef = useRef(assignItemsToClassHandler);
  assignRef.current = assignItemsToClassHandler;
  const handlerRef = useRef<(className: string) => void>();

  useEffect(() => {
    handlerRef.current = (className: string) => {
      assignRef.current?.(className);
    };
  }, []);
  useEffect(() => {
    if (!onAssign) return;
    onAssign(handlerRef.current!);
  }, [onAssign]);

  if (loading || !step) return <div>Loading...</div>;

  const displayButtons = currentStep.nextButton || currentStep.prevButton;

  if (!isLabelPropagationEnabled) {
    return (
      <div className={styles.notEnabled}>
        This feature isn’t available right now.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.loaderWrapper}>
        {" "}
        <VLColorfulLoader />
      </div>
    );
  }
  return (
    <div className={styles.wrapper}>
      {modal && (
        <VLActionModal
          onClick={modal.onClick}
          isOpen={modal.isOpen}
          variant={modal.variant}
          onClose={modal.onClose}
          headers={modal.headers}
          actionLabel={modal.actionLabel}
        />
      )}
      <div className={styles.content}>
        {currentStep.title && (
          <>
            <div className={styles.title}>
              <div className={styles.titleAndButton}>
                <span className={styles.titleText}>
                  {currentStep.title.text}
                </span>
                {currentStep.title.icon && currentStep.title.icon}
              </div>
              {currentStep?.title?.count !== undefined && (
                <VLCount count={currentStep.title.count} />
              )}
            </div>
            <div className={styles.subTitle}>
              <span className={styles.subTitleText}>
                {currentStep.title.subText1}
              </span>
              <span className={styles.subTitleText}>
                {currentStep.title.subText2}
              </span>
            </div>
          </>
        )}
        <div className={styles.stepContent} ref={stepContentRef}>
          {currentStep.content}
        </div>
      </div>

      {isContentOverflowing && <div className={styles.overflow}></div>}

      {displayButtons && step && (
        <div className={styles.buttonsWrapper}>
          <div className={styles.buttons}>
            {step > 1 && currentStep.prevButton && (
              <VLButton
                danger={true}
                onClick={currentStep.prevButton.onClick}
                disabled={currentStep.prevButton.disabled}
                text={currentStep.prevButton.text}
                type={currentStep.prevButton.type}
                tooltip={currentStep.prevButton.tooltip}
              />
            )}
            {currentStep.nextButton && (
              <Tooltip title={currentStep.nextButton.tooltip}>
                <div
                  className={classNames(styles.nextButtonWrapper, {
                    [styles.allowClickOnDisable]:
                      currentStep.nextButton.disabled,
                  })}
                  onClick={() => {
                    if (currentStep?.nextButton?.disabled) {
                      toggleBlinkCard();
                    }
                  }}
                >
                  <VLButton
                    type={"primary"}
                    disabled={currentStep.nextButton.disabled}
                    onClick={currentStep.nextButton.onClick}
                    isGradient={currentStep.nextButton.isGradient}
                    data-testid="next-button"
                    text={currentStep.nextButton.text}
                    tooltip={currentStep.nextButton.tooltip}
                  />
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
